/**
 * Secure Logger - Production-Safe Logging
 * 
 * CRITICAL: This logger ensures:
 * - No PII in logs (memberId, userId, email, etc.)
 * - No sensitive data logged
 * - Uses Sentry in production
 * - Console logs only in development
 */

// Sentry import - check if available (optional dependency)
let Sentry = null;
try {
  // Use dynamic import to avoid build errors if Sentry not installed
  if (typeof window !== 'undefined') {
    // Browser environment - Sentry may be available
    // Note: Sentry should be initialized separately in the app
    // This is just for capturing errors if Sentry is available
  }
} catch (e) {
  // Sentry not installed - will skip Sentry logging
  Sentry = null;
}

// Helper to safely call Sentry if available
function captureSentryError(message, error = null) {
  if (typeof window !== 'undefined' && window.Sentry) {
    if (error instanceof Error) {
      window.Sentry.captureException(error, {
        extra: { message }
      });
    } else {
      window.Sentry.captureMessage(message, 'error');
    }
  }
}

function captureSentryMessage(message, level = 'warning') {
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.captureMessage(message, level);
  }
}

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Redact PII from log messages
 * @param {string} message - Log message
 * @returns {string} Redacted message
 */
function redactPII(message) {
  if (!message || typeof message !== 'string') {
    return message;
  }
  
  // Redact email addresses
  message = message.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]');
  
  // Redact member IDs (various formats)
  message = message.replace(/\b(memberId|userId|member_id|user_id)[:=]\s*['"]?[a-zA-Z0-9]{10,}['"]?/gi, '$1=[ID_REDACTED]');
  message = message.replace(/member:\s*['"]?[a-zA-Z0-9]{10,}['"]?/gi, 'member=[ID_REDACTED]');
  message = message.replace(/user:\s*['"]?[a-zA-Z0-9]{10,}['"]?/gi, 'user=[ID_REDACTED]');
  
  // Redact Auth0 sub
  message = message.replace(/auth0\|[a-zA-Z0-9_-]+/gi, '[AUTH0_ID_REDACTED]');
  
  // Redact passcodes (any 4-8 digit numbers that might be passcodes)
  message = message.replace(/\bpasscode[:=]\s*\d{4,8}\b/gi, 'passcode=[REDACTED]');
  
  return message;
}

/**
 * Secure log - replaces console.log
 * @param {string} message - Log message
 * @param {any} data - Optional data (will be redacted)
 */
export function secureLog(message, data = null) {
  if (isDevelopment) {
    const redactedMessage = redactPII(String(message));
    if (data) {
      console.log(redactedMessage, data);
    } else {
      console.log(redactedMessage);
    }
  }
  // In production, don't log to console
}

/**
 * Secure error log - replaces console.error
 * @param {string} message - Error message
 * @param {Error|any} error - Error object or data
 */
export function secureError(message, error = null) {
  const redactedMessage = redactPII(String(message));
  
  if (isDevelopment) {
    if (error) {
      console.error(redactedMessage, error);
    } else {
      console.error(redactedMessage);
    }
  }
  
  // In production, send to Sentry (if available)
  if (!isDevelopment) {
    captureSentryError(redactedMessage, error);
  }
}

/**
 * Secure warn log - replaces console.warn
 * @param {string} message - Warning message
 * @param {any} data - Optional data
 */
export function secureWarn(message, data = null) {
  const redactedMessage = redactPII(String(message));
  
  if (isDevelopment) {
    if (data) {
      console.warn(redactedMessage, data);
    } else {
      console.warn(redactedMessage);
    }
  }
  
  // In production, send important warnings to Sentry (if available)
  if (!isDevelopment && message.includes('SECURITY')) {
    captureSentryMessage(redactedMessage, 'warning');
  }
}

/**
 * Security event log - for security-related events
 * @param {string} event - Event type
 * @param {string} message - Event message
 */
export function securityLog(event, message) {
  const redactedMessage = redactPII(String(message));
  
  // Always log security events to Sentry (if available)
  captureSentryMessage(`[SECURITY] ${event}: ${redactedMessage}`, 'warning');
  
  if (isDevelopment) {
    console.warn(`[SECURITY] ${event}:`, redactedMessage);
  }
}

export default {
  log: secureLog,
  error: secureError,
  warn: secureWarn,
  security: securityLog
};

